/**
 * CoverArtSettingsContext -- Provides cover art provider state to artwork hooks.
 *
 * Consumed by useTmdbImages, useTmdbBatch, useTvdbImages, useTvdbBatch, and
 * the unified useCoverArtBatch/useCoverArtImages hooks to gate API calls and
 * route to the correct provider per content category.
 *
 * Default: tmdbEnabled = false, tvdbEnabled = false, both providers = "none"
 * (server artwork is served as-is until an admin opts into a provider).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CoverArtSettingsContextValue {
  tmdbEnabled: boolean;
  tvdbEnabled: boolean;
  movieProvider: "tmdb" | "tvdb" | "none";
  seriesProvider: "tmdb" | "tvdb" | "none";
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CoverArtSettingsCtx = createContext<CoverArtSettingsContextValue>({
  tmdbEnabled: false,
  tvdbEnabled: false,
  movieProvider: "none",
  seriesProvider: "none",
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useCoverArtContext = (): CoverArtSettingsContextValue =>
  useContext(CoverArtSettingsCtx);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const CoverArtSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [tmdbEnabled, setTmdbEnabled] = useState(false);
  const [tvdbEnabled, setTvdbEnabled] = useState(false);
  const [movieProvider, setMovieProvider] = useState<"tmdb" | "tvdb" | "none">("none");
  const [seriesProvider, setSeriesProvider] = useState<"tmdb" | "tvdb" | "none">("none");

  // Fetch initial settings from the API
  useEffect(() => {
    fetch("/api/streaming/settings/cover-art", {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((json: { ok?: boolean; data?: CoverArtSettingsContextValue }) => {
        if (json.ok && json.data) {
          setTmdbEnabled(json.data.tmdbEnabled === true);
          setTvdbEnabled(json.data.tvdbEnabled === true);
          if (json.data.movieProvider) setMovieProvider(json.data.movieProvider);
          if (json.data.seriesProvider) setSeriesProvider(json.data.seriesProvider);
        }
      })
      .catch(() => {
        /* keep defaults */
      });
  }, []);

  // Listen for setting changes dispatched by CoverArtSettings panel on save
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CoverArtSettingsContextValue>).detail;
      if (detail) {
        setTmdbEnabled(detail.tmdbEnabled === true);
        setTvdbEnabled(detail.tvdbEnabled === true);
        if (detail.movieProvider) setMovieProvider(detail.movieProvider);
        if (detail.seriesProvider) setSeriesProvider(detail.seriesProvider);
      }
    };
    window.addEventListener("cover-art-settings-changed", handler);
    // Keep legacy listener for backward compat with any remaining dispatchers
    const legacyHandler = (e: Event) => {
      setTmdbEnabled((e as CustomEvent<boolean>).detail);
    };
    window.addEventListener("cover-art-tmdb-changed", legacyHandler);
    return () => {
      window.removeEventListener("cover-art-settings-changed", handler);
      window.removeEventListener("cover-art-tmdb-changed", legacyHandler);
    };
  }, []);

  return (
    <CoverArtSettingsCtx.Provider value={{ tmdbEnabled, tvdbEnabled, movieProvider, seriesProvider }}>
      {children}
    </CoverArtSettingsCtx.Provider>
  );
};
